import type {
    PaymentAnchor,
    PaymentTrancheTemplate,
    PaymentTrancheType,
} from '@api/mother-order';

/** One payment-terms row while it is being typed: a number field may be empty. */
export type TrancheDraft = {
    label: string;
    type: PaymentTrancheType;
    percentage: number | '';
    anchor: PaymentAnchor;
    offsetDays: number | '';
};

export const TYPE_OPTIONS: Array<{ value: PaymentTrancheType; label: string }> = [
    { value: 'ADVANCE', label: 'Advance' },
    { value: 'BALANCE', label: 'Balance' },
    { value: 'CUSTOM', label: 'Custom' },
];

export const ANCHOR_OPTIONS: Array<{ value: PaymentAnchor; label: string }> = [
    { value: 'ORDER_DATE', label: 'Order date' },
    { value: 'READINESS', label: 'Goods ready' },
    { value: 'DISPATCH', label: 'Dispatch' },
    { value: 'ARRIVAL', label: 'At destination port' },
    { value: 'DELIVERY', label: 'Delivered' },
    { value: 'FIXED', label: 'Fixed date' },
];

export const DEFAULT_ROW: Record<PaymentTrancheType, Partial<TrancheDraft>> = {
    ADVANCE: { label: 'Advance', anchor: 'ORDER_DATE' },
    BALANCE: { label: 'Balance', anchor: 'DISPATCH' },
    CREDIT: { label: 'Credit', anchor: 'DISPATCH' },
    CUSTOM: { label: '', anchor: 'DISPATCH' },
};

export const draftsFromTemplate = (
    template: PaymentTrancheTemplate[] | null | undefined,
): TrancheDraft[] =>
    (template ?? []).map((t) => ({
        label: t.label,
        type: t.type,
        percentage: t.percentage,
        anchor: t.anchor,
        offsetDays: t.offsetDays ?? 0,
    }));

/** Rows with no percentage are rows nobody finished; they are not terms. */
export const templateFromDrafts = (drafts: TrancheDraft[]): PaymentTrancheTemplate[] =>
    drafts
        .filter((t) => t.percentage !== '' && Number(t.percentage) > 0)
        .map((t) => ({
            label: t.label.trim() || t.type,
            type: t.type,
            percentage: Number(t.percentage),
            anchor: t.anchor,
            offsetDays: Number(t.offsetDays) || 0,
        }));

export const draftsSum = (drafts: TrancheDraft[]): number =>
    drafts.reduce((s, t) => s + (Number(t.percentage) || 0), 0);

export const sumsToHundred = (drafts: TrancheDraft[]): boolean =>
    Math.abs(draftsSum(drafts) - 100) <= 0.01;

/** The first row of a deal is its advance; whatever follows is balance. */
export const nextRow = (existing: TrancheDraft[]): TrancheDraft => {
    const type: PaymentTrancheType = existing.length === 0 ? 'ADVANCE' : 'BALANCE';
    return {
        label: '',
        type,
        percentage: '',
        anchor: 'DISPATCH',
        offsetDays: 0,
        ...DEFAULT_ROW[type],
    };
};
